import { useCallback, useEffect, useRef, useState } from 'react';
import { defaultRenderImage, type RenderImage } from '../../../utils/image/renderImage';
import { CaretIcon } from './icons';

export interface RichSelectOption {
  value: string;
  label: string;
  /** linha secundária (ex.: preço, endereço). */
  description?: string;
  /** miniatura/logo exibida à esquerda do label. */
  imageSrc?: string;
  /** 'thumb' = retângulo com foto (modelo/versão) · 'logo' = quadrado contain (marca). */
  imageKind?: 'thumb' | 'logo';
}

interface RichSelectProps {
  ariaLabel: string;
  value: string;
  options: RichSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** exibe monograma com a inicial quando a opção não tem imagem. */
  monogram?: boolean;
  /** injetada pelo wrapper: `<img>` (builder) ou `next/image` (renderer). */
  renderImage?: RenderImage;
}

/** Acima disso, o select mostra um campo de busca pra filtrar as opções. */
const SEARCH_THRESHOLD = 7;

const OptionMedia = ({
  option,
  monogram,
  renderImage,
}: {
  option: RichSelectOption;
  monogram?: boolean;
  renderImage: RenderImage;
}) => {
  if (option.imageSrc) {
    const isLogo = option.imageKind === 'logo';
    return (
      <span className={`lpfs-rs-media${isLogo ? ' is-logo' : ''}`}>
        {renderImage({
          src: option.imageSrc,
          alt: '',
          width: isLogo ? 64 : 96,
          height: 64,
          loading: 'lazy',
        })}
      </span>
    );
  }
  if (monogram) return <span className="lpfs-rs-monogram">{option.label.charAt(0).toUpperCase()}</span>;
  return null;
};

/** Select estilizado do bloco (substitui o <select> nativo): trigger + listbox
    com slot de mídia por opção. Quando a lista é grande, exibe busca por
    digitação (filtra as opções, como um combobox). */
export const RichSelect = ({
  ariaLabel,
  value,
  options,
  onChange,
  placeholder = 'Selecione',
  disabled = false,
  monogram = false,
  renderImage = defaultRenderImage,
}: RichSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const selected = options.find((o) => o.value === value) || null;
  const hasMedia = monogram || options.some((o) => o.imageSrc);
  const searchable = options.length > SEARCH_THRESHOLD;

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  const close = useCallback(() => setOpen(false), []);

  // Reset da busca ao abrir/fechar; foca o campo ao abrir.
  useEffect(() => {
    setQuery('');
    if (open && searchable) {
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const pick = (val: string) => {
    onChange(val);
    close();
  };

  return (
    <div className={`lpfs-rs${open ? ' is-open' : ''}${hasMedia ? ' has-media' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="lpfs-rs-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {selected ? (
          <span className="lpfs-rs-current">
            <OptionMedia option={selected} monogram={monogram} renderImage={renderImage} />
            <span className="lpfs-rs-texts">
              <span className="lpfs-rs-label">{selected.label}</span>
              {selected.description && <span className="lpfs-rs-desc">{selected.description}</span>}
            </span>
          </span>
        ) : (
          <span className="lpfs-rs-placeholder">{placeholder}</span>
        )}
        <span className="lpfs-rs-caret">
          <CaretIcon />
        </span>
      </button>

      {open && (
        <ul className="lpfs-rs-panel" role="listbox" aria-label={ariaLabel}>
          {searchable && (
            <li
              role="presentation"
              style={{ position: 'sticky', top: 0, zIndex: 1, padding: 8, background: 'var(--lpfs-card-bg, #fff)' }}
            >
              <input
                ref={searchRef}
                className="lpfs-input"
                type="text"
                autoComplete="off"
                placeholder="Buscar…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && filtered.length) {
                    e.preventDefault();
                    pick(filtered[0].value);
                  }
                }}
              />
            </li>
          )}

          {filtered.map((o) => {
            const sel = o.value === value;
            return (
              <li key={o.value} role="option" aria-selected={sel}>
                <button
                  type="button"
                  className={`lpfs-rs-option${sel ? ' is-selected' : ''}`}
                  onClick={() => pick(o.value)}
                >
                  <OptionMedia option={o} monogram={monogram} renderImage={renderImage} />
                  <span className="lpfs-rs-texts">
                    <span className="lpfs-rs-label">{o.label}</span>
                    {o.description && <span className="lpfs-rs-desc">{o.description}</span>}
                  </span>
                </button>
              </li>
            );
          })}

          {searchable && filtered.length === 0 && (
            <li role="presentation" style={{ padding: '12px 14px', fontSize: 14, color: 'var(--lpfs-muted)' }}>
              Nenhum resultado para “{query}”.
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
